import { useState } from 'react'
import { useCV, type Experience as ExperienceItem } from '../hooks/useCV'
import Section from './Section'

const VISIBLE = 4

const Entry = ({ exp }: { exp: ExperienceItem }) => {
  const [open, setOpen] = useState(false)
  const hidden = exp.description.length - VISIBLE
  const items = open || hidden <= 0 ? exp.description : exp.description.slice(0, VISIBLE)

  return (
    <article className="grid md:grid-cols-12 gap-x-8 gap-y-3 py-10 border-b border-rule first:pt-0 last:border-b-0">
      <div className="md:col-span-4">
        <h3 className="font-serif text-2xl font-medium leading-tight">{exp.company}</h3>
        <p className="mt-1 text-sm text-mute">{exp.period}</p>
      </div>
      <div className="md:col-span-8">
        <p className="font-medium">{exp.title}</p>
        <ul className="mt-3 space-y-3 text-[0.9375rem] leading-relaxed text-mute">
          {items.map((item, i) => (
            <li key={i} className="flex gap-3">
              <span aria-hidden className="text-accent">–</span>
              <span>{item}</span>
            </li>
          ))}
        </ul>
        {hidden > 0 && (
          <button
            type="button"
            onClick={() => setOpen(!open)}
            aria-expanded={open}
            className="mt-4 text-sm font-medium text-accent underline underline-offset-4 hover:no-underline"
          >
            {open ? 'Show less' : `Show ${hidden} more`}
          </button>
        )}
      </div>
    </article>
  )
}

const Experience = () => {
  const { experiences, education, certificates } = useCV()

  return (
    <>
      <Section id="experience" title="Experience">
        {experiences.map((exp) => (
          <Entry key={`${exp.company}-${exp.period}`} exp={exp} />
        ))}
      </Section>

      <Section id="education" title="Education">
        {education.map((edu) => (
          <div key={edu.school} className="grid md:grid-cols-12 gap-x-8 gap-y-1 py-5 border-b border-rule first:pt-0 last:border-b-0">
            <h3 className="md:col-span-4 font-serif text-xl font-medium">{edu.school}</h3>
            <p className="md:col-span-5 text-mute">{edu.major}</p>
            <p className="md:col-span-3 text-sm text-mute md:text-right">{edu.period}</p>
          </div>
        ))}
      </Section>

      {certificates.length > 0 && (
        <Section id="certificates" title="Certificates">
          {certificates.map((cert) => (
            <div key={cert.name} className="grid md:grid-cols-12 gap-x-8 gap-y-1 py-5 border-b border-rule first:pt-0 last:border-b-0">
              <h3 className="md:col-span-5 font-serif text-xl font-medium">{cert.name}</h3>
              <p className="md:col-span-4 text-mute">{cert.issuer}</p>
              <div className="md:col-span-3 md:text-right text-sm text-mute">
                <p>{cert.date}</p>
                {cert.url && (
                  <a href={cert.url} target="_blank" rel="noopener noreferrer" className="mt-1 inline-block font-medium text-accent underline underline-offset-4 hover:no-underline">
                    View certificate
                  </a>
                )}
              </div>
            </div>
          ))}
        </Section>
      )}
    </>
  )
}

export default Experience
